import { useState, useEffect, useCallback } from "react";
import { Select, Tooltip, ConfigProvider, Spin } from "antd";
import { Option } from "antd/es/mentions";
import { InputNumber } from "syngenta-digital-cropwise-react-ui-kit";
import infoIcon from "../../../assets/images/info.svg";
import { useNewTrialState } from "../../../pages/new-trail/hooks/NewTrialStateProvider";
import { useTranslation } from "react-i18next";
import { formatTranslation } from "../../utils/translationUtils";
import "./style.less";
import AxiosInstance from "../../utils/axios";
import PlatFormAPIAxiosInstance from "../../utils/platFormAxios";
import { DropdownEmptyState } from "../DropdownEmptyState";
import { restrictToTwoDecimals } from "../../../pages/create-protocol/utils";
import { useTrialsRouter } from "../../custom-hooks/useTrialsRouter";

const StandardSeedRate = () => {
  const { t } = useTranslation();
  const {
    ratesDosage,
    setRatesDosage,
    trialInfo,
    setIsStandardDataValid,
    trialProtocolData,
  } = useNewTrialState();
  const { orgId, workspaceId } = useTrialsRouter();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  const extraHeaders = {
    "org-id": orgId,
    "workspace-id": workspaceId ?? "",
  };

  const getProtocolData = useCallback(async () => {
    try {
      const protocolData = await AxiosInstance.get(
        `/protocol/${trialInfo?.protocol_id}`,
        { headers: extraHeaders }
      );

      const payload = {
        localized_crop_id: [protocolData?.data?.crop_details?.crop_id],
        show_cononical: true,
      };

      const cropData = await PlatFormAPIAxiosInstance.post(
        "/catalog/seeds/query?page=1&size=100",
        payload
      );

      setProducts(cropData?.data?.content);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }, [trialInfo?.protocol_id]);

  useEffect(() => {
    if (trialInfo?.protocol_id) {
      setLoading(true);

      getProtocolData()
        .catch((error) => {
          console.error("Error fetching data:", error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [trialInfo?.protocol_id, getProtocolData]);

  const selectedProductValue = products?.some(
    (product) => product?.id === ratesDosage?.standard_seed_product_id
  )
    ? ratesDosage?.standard_seed_product_id
    : ratesDosage?.standard_product ?? undefined;

  useEffect(() => {
    if (
      ratesDosage.standard_seed_rate == null ||
      ratesDosage.standard_seed_rate === 0 ||
      !ratesDosage.standard_product
    ) {
      setIsStandardDataValid(false);
    } else {
      setIsStandardDataValid(true);
    }
  }, [
    ratesDosage.standard_seed_rate,
    ratesDosage.standard_product,
    setIsStandardDataValid,
  ]);

  const handleStandardSeedRateChange = (seedRate) => {
    const updatedRatesAndDosage = {
      ...ratesDosage,
      standard_seed_rate: seedRate,
    };

    setRatesDosage(updatedRatesAndDosage);
  };

  const handleStandardProductChange = (product) => {
    const productName = products?.find(
      (p) => p.id === product
    )?.commercial_name;

    const updatedRatesAndDosage = {
      ...ratesDosage,
      standard_product: productName,
      standard_seed_product_id: product,
    };

    setRatesDosage(updatedRatesAndDosage);
  };

  const shouldDisplayRateError = () => {
    if (
      ratesDosage.standard_product &&
      (!ratesDosage.standard_seed_rate ||
        ratesDosage.standard_seed_rate == null)
    ) {
      return true;
    }

    return false;
  };

  const dropdownRender = (menu) => (
    <div>
      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: "10px",
          }}
        >
          <Spin size="small" />
        </div>
      ) : (
        menu
      )}
    </div>
  );

  const seedRateUnit =
    ratesDosage.unit_of_rate ||
    trialProtocolData?.product_details?.seeds?.measure_unit;

  return (
    <div className="standard-seedrate-main">
      <div className="seedrate-header">
        <p className="seedrate-heading">
          {formatTranslation(
            t("trials.plotDesign.trialPlots.table.standardSeedRate")
          )}
        </p>

        <Tooltip
          placement="right"
          title={formatTranslation(
            t("trials.plotDesign.standardSeedRate.tooltip")
          )}
        >
          <img src={infoIcon} alt="info-icon" className="seedrate-info-icon" />
        </Tooltip>
      </div>

      {/* Flat flex row — no Form.Item label prop so Ant Design can't push inputs out of alignment */}
      <div className="seedrate-selections">
        {/* Product column */}
        <div className="select-product">
          <label
            className="seedrate-field-label"
            htmlFor="standard-seed-product"
          >
            Product
          </label>

          <ConfigProvider renderEmpty={DropdownEmptyState}>
            <Select
              id="standard-seed-product"
              showSearch
              placeholder={formatTranslation(
                t("trials.plotDesign.trialPlots.select")
              )}
              value={selectedProductValue}
              onChange={(product) => {
                handleStandardProductChange(product);
              }}
              className="selected-product"
              popupRender={dropdownRender}
              loading={loading}
            >
              {products.length > 0 &&
                products
                  .slice()
                  .sort((a, b) =>
                    a?.commercial_name?.localeCompare(b?.commercial_name)
                  )
                  .map((product) => (
                    <Option key={product?.id} value={product?.id}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span>{product?.commercial_name}</span>

                        <span
                          style={{
                            fontSize: "12px",
                            color: "#999",
                            marginLeft: "8px",
                          }}
                        >
                          {product?.brand || "N/A"} •{" "}
                          {product?.manufacturer || "N/A"}
                        </span>
                      </div>
                    </Option>
                  ))}
            </Select>
          </ConfigProvider>

          {shouldDisplayRateError() && (
            <span
              className="error-message"
              style={{ marginTop: "4px", display: "flex" }}
            >
              {formatTranslation(
                t("trials.plotDesign.trialPlots.errorText.rate")
              )}
            </span>
          )}
        </div>

        {/* Seed Rate column */}
        <div className="input-seedrate">
          <label className="seedrate-field-label" htmlFor="standard-seed-rate">
            {`Seed Rate (${seedRateUnit})`}
          </label>

          <InputNumber
            id="standard-seed-rate"
            min={0}
            precision={2}
            step={0.01}
            style={{ height: "40px" }}
            value={ratesDosage.standard_seed_rate ?? 0}
            onChange={(seedRate) => {
              handleStandardSeedRateChange(seedRate);
            }}
            onKeyDown={restrictToTwoDecimals}
            className="input-number seeds-rate-input-uom"
          />
        </div>
      </div>
    </div>
  );
};

export default StandardSeedRate;
